import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { PageviewTracker } from "./analytics/PageviewTracker";
import { initAnalytics } from "./analytics/posthog";
import { initSentry } from "./analytics/sentry";
import { AgentationToolbar } from "./components/AgentationToolbar";
import { AppErrorFallback } from "./components/AppErrorFallback";
import { Layout } from "./components/AppShell";
import { ErrorBoundary } from "./components/Layout/ErrorBoundary";
import { Toaster } from "./components/ui/sonner";
import { NotFound } from "./pages/NotFound";
import type { YaasAppConfig } from "./types/app";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 2
        }
    }
});

/**
 * Bootstrap function for YaaS apps (Issuer Console, Client Console).
 * Same as createApp but without WalletProvider and Solana infrastructure.
 */
export async function createYaasApp({
    appName,
    rootSelector = "#root",
    routes,
    unprotectedRoutes = [],
    components: { layout: LayoutComponent = Layout } = {},
    pages: { notFound: NotFoundPage = NotFound } = {},
    setup
}: YaasAppConfig): Promise<void> {
    initSentry({ appName });
    initAnalytics();

    if (setup) {
        await setup();
    }

    function RouterImpl(): React.ReactElement {
        const routesLen = routes.length;
        const protectedRouteElements = new Array(routesLen + 1);

        for (let i = 0; i < routesLen; i++) {
            const route = routes[i];

            protectedRouteElements[i] = (
                <Route
                    key={route.key}
                    path={route.path}
                    element={<route.component />}
                />
            );
        }

        protectedRouteElements[routesLen] = (
            <Route
                path="*"
                element={<NotFoundPage key="NotFound" />}
                key={"CatchAll"}
            />
        );

        return (
            <BrowserRouter>
                <PageviewTracker />
                <Routes>
                    {/* Unprotected routes — rendered without the layout shell */}
                    {unprotectedRoutes.map((route) => (
                        <Route
                            key={route.key}
                            path={route.path}
                            element={<route.component />}
                        />
                    ))}

                    {/* Protected routes — wrapped in the layout shell */}
                    <Route
                        element={<LayoutComponent key="Layout" />}
                        children={protectedRouteElements}
                    />
                </Routes>
            </BrowserRouter>
        );
    }

    const Router = React.memo(RouterImpl);

    function App(): React.ReactElement {
        return (
            <QueryClientProvider client={queryClient}>
                <Router />
                <Toaster />
                <AgentationToolbar />
            </QueryClientProvider>
        );
    }

    const rootElement = document.querySelector(rootSelector);
    if (!rootElement) {
        throw new Error("Root element not found");
    }

    const root = createRoot(rootElement);
    root.render(
        <ErrorBoundary fallback={AppErrorFallback}>
            <App />
        </ErrorBoundary>
    );
}
